import { CustomSelect, HeaderPage, InputForm, LinkButton, Paginator, SwitchDefault, TableGeneric } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IColumn, IListPaginated } from '@interfaces/index'
import { REAL_ESTATE_TYPE_FILTER } from '@interfaces/realEstates/enums/realEstateType'
import { IActiveAction } from '@interfaces/realEstates/iActiveStatus'
import { IRealEstate } from '@interfaces/realEstates/iRealEstate'
import { ISearchRealEstateFilters } from '@interfaces/realEstates/iRealEstateFilters'
import { errorHandler } from '@services/index'
import RealEstateService from '@services/requests/realEstateService'
import { totalPages, typeTranslation } from '@utils/index'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineSearch } from 'react-icons/ai'
import { BiFilterAlt } from 'react-icons/bi'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function RealEstates(): React.ReactElement {
    const queryPage = useQuery().get('page') ?? '1'
    const name = useQuery().get('name') as string
    const type = useQuery().get('type') as REAL_ESTATE_TYPE_FILTER
    function useQuery(): URLSearchParams {
        return new URLSearchParams(useLocation().search)
    }
    const navigate = useNavigate()

    const [data, setData] = React.useState<IListPaginated<IRealEstate>>(defaultPagination)
    const [isLoading, setIsLoading] = React.useState(false)

    const { handleSubmit, control } = useForm<ISearchRealEstateFilters>({
        defaultValues: {
            name: name ?? '',
            type: type ?? REAL_ESTATE_TYPE_FILTER.ALL
        }
    })
    const types = [
        { title: 'Todos', value: REAL_ESTATE_TYPE_FILTER.ALL },
        { title: 'Em Breve', value: REAL_ESTATE_TYPE_FILTER.COMMING_SOON },
        { title: 'Empreendimento', value: REAL_ESTATE_TYPE_FILTER.VENTURE },
        { title: 'Lançamento', value: REAL_ESTATE_TYPE_FILTER.RELEASE },
        { title: 'Selecionado', value: REAL_ESTATE_TYPE_FILTER.SELECTED },
    ]

    function init(): void {
        if (queryPage) {
            getList(parseInt(queryPage), undefined, name, type)
        }
        else
            getList(1)
    }

    React.useEffect(() => {
        init()
    }, [queryPage, name, type])

    async function updateStatus(id: number, activeAction: IActiveAction): Promise<boolean> {
        try {
            const response = await RealEstateService.updateStatus(id, activeAction)
            toast(response.message, { type: 'success' })
            return response.active
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
            return !activeAction.active
        }
    }

    async function getList(page?: number, perPage?: number, name?: string, type?: REAL_ESTATE_TYPE_FILTER): Promise<void> {
        setIsLoading(true)
        try {
            const response = await RealEstateService.list(page, perPage, name, type)
            if (page && page !== 1 && response.items.length <= 0) {
                navigate(`?page=${page - 1}`)
            } else {
                setData(response)
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function submit(values: ISearchRealEstateFilters): void {
        navigate(
            {
                pathname: '/imoveis',
                search: createSearchParams({
                    page: '1',
                    name: values.name,
                    type: values.type
                }).toString()
            }
        )
    }

    const columns: IColumn<IRealEstate>[] = React.useMemo<IColumn<IRealEstate>[]>(
        () => [
            {
                header: 'Posição',
                dataIndex: 'listPosition',
            },
            {
                header: 'Nome',
                dataIndex: 'name',
                size: '40%'
            },
            {
                header: 'Tipo',
                dataIndex: 'type',
                render: (item) => typeTranslation(item.type)
            },
            {
                header: 'Status',
                dataIndex: 'active',
                align: 'align-center',
                render: (item) => {
                    const [checked, setChecked] = React.useState(item.active)
                    const [isDisabledSwitch, setIsDisabledSwitch] = React.useState(false)
                    return (
                        <div className='box-action-switch'>
                            <div>
                                <SwitchDefault
                                    onChange={async (event) => {
                                        setIsDisabledSwitch(true)
                                        const res = await updateStatus(item.id, { active: event.target.checked })
                                        setIsDisabledSwitch(false)
                                        setChecked(res)
                                    }
                                    }
                                    color='secondary'
                                    checked={checked}
                                    disabled={isDisabledSwitch}
                                    title={'Ativar/Desativar'}
                                />
                                <p className='small-2'>{checked ? 'Ativo' : 'Inativo'}</p>
                            </div>
                        </div>
                    )
                }
            },
            {
                header: 'Ação',
                dataIndex: 'actions',
                align: 'align-center',
                size: '8rem',
                render: (item) => (
                    <LinkButton title='Editar' path={`/imoveis/${item.id}/editar?page=${queryPage}`} />
                ),
            },
        ],
        [data]
    )

    return (
        <div className="card">
            <div className="real-estates">
                <HeaderPage title="Imóveis" textButton="Novo Imóvel" onClickButton={() => navigate('/imoveis/cadastrar')} />
                <div>
                    <form className='real-estate-filters' onSubmit={handleSubmit(submit)}>
                        <Controller
                            control={control}
                            name='name'
                            render={({ field: { value, onChange } }) => (
                                <InputForm
                                    size="small"
                                    value={value}
                                    onChange={onChange}
                                    onInputCallBack={() => {
                                        handleSubmit(submit)()
                                    }}
                                    placeholder="Buscar por nome"
                                    style={{ width: '23rem', marginRight: '1rem' }}
                                    InputProps={{
                                        endAdornment: (
                                            <button type='submit' className='button-search'>
                                                <AiOutlineSearch />
                                            </button>
                                        )
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name='type'
                            control={control}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <CustomSelect
                                        label='Tipo'
                                        placeholder='Filtrar por tipo'
                                        name='type'
                                        value={value}
                                        defaultValue={value}
                                        onChange={(e) => { onChange(e); handleSubmit(submit)() }}
                                        style={{ width: '14rem' }}
                                        options={types}
                                        inputProps={{
                                            startAdornment: (
                                                <BiFilterAlt style={{ marginRight: '8px' }} />
                                            )
                                        }}
                                    />
                                )
                            }}
                        />
                    </form>
                    <div>
                        <TableGeneric data={data.items} columns={columns} isLoading={isLoading} />
                        <div className="paginator">
                            <Paginator
                                maxElements={7}
                                current={data.page}
                                total={totalPages(data.count, data.perPage)}
                                onClick={(page) => navigate(
                                    {
                                        pathname: '/imoveis',
                                        search: createSearchParams({
                                            page: page.toString(),
                                            name: name ? name : '',
                                            type: type ? type : REAL_ESTATE_TYPE_FILTER.ALL
                                        }).toString()
                                    }
                                )} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
